import {
  ButtonType,
  DialogFooterButtonsWidget,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  TabStyles
} from '@tableau/widgets';
import * as React from 'react';
import { classes } from 'typestyle';

import { CommonStyles } from '~common/react/CommonStyles';
import { Messages } from '~src/codegen/Localize';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { cssClass } from '~src/style';
import { IPublishedConnectionDetailInfo } from '~src/ts/models/IPublishedConnectionInfo';
import {
  IPublishedConnectionTaskInfo,
  IPublishedConnectionTaskTableInfo,
  PublishedConnectionExtractRefreshLevel
} from '~src/ts/models/IPublishedConnectionTaskInfo';
import { ExtractTaskType } from '~src/ts/ServerApi/types/IExtractTask';

import { PublishedConnectionScheduledTaskTableSelectionDialog } from './PublishedConnectionScheduledTaskTableSelectionDialog';
import { PublishedConnectionScheduledTaskTablesGrid } from './PublishedConnectionScheduledTaskTablesGrid';
import { PublishedConnectionTaskRefreshTypeDropdown } from './PublishedConnectionTaskRefreshTypeDropdown';
import { PublishedConnectionTaskSchedulePicker } from './PublishedConnectionTaskSchedulePicker';

const cssPrefix = 'PublishedConnectionCreateTaskDialogContent';

const rootClass = cssClass(cssPrefix, 'root', {
  display: 'flex',
  flexDirection: 'column'
});

const topPaneClass = cssClass(cssPrefix, 'topPane', {
  flex: 'none'
});

const dialogMessageClass = cssClass(cssPrefix, 'dialogMessage', {
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

const paddingBottomClass = cssClass(cssPrefix, 'paddingBottom', {
  paddingBottom: CommonStyles.Sizes.LargeSpace,
});

const refreshTypeButtonClass = cssClass(cssPrefix, 'refreshTypeButton', {
  width: '250px'
});

const bottomPaneClass = cssClass(cssPrefix, 'bottomPane', {
  flex: 1,
  flexDirection: 'column'
});

const footerClass = cssClass(cssPrefix, 'footerClass', {
  flex: 'none',
});

const tablesContainerClass = cssClass(cssPrefix, 'tablesContainer', {
  borderLeft: `1px solid ${CommonStyles.Colors.Grey203}`,
  borderRight: `1px solid ${CommonStyles.Colors.Grey203}`,
  borderBottom: `1px solid ${CommonStyles.Colors.Grey203}`,
  marginBottom: TabStyles.Spacing.LargeSpace,
  flex: 1,
  overflow: 'auto',
  height: 250
});

const editTableLinkClass = cssClass(cssPrefix, 'editTableLink', {
  paddingBottom: TabStyles.Spacing.DefaultSpace,
  fontWeight: 'bold'
});

function createRadioButtonOptions(): RadioButtonWithLabelOption<PublishedConnectionExtractRefreshLevel>[] {
  return [
    {
      value: PublishedConnectionExtractRefreshLevel.Database,
      testId: 'published-connection-refresh-level-database',
      label: Messages.publishedConnectionTableSyncRefreshMessage()
    },
    {
      value: PublishedConnectionExtractRefreshLevel.Table,
      testId: 'published-connection-refresh-level-table',
      label: Messages.publishedConnectionTableIndependentRefreshMessage()
    }
  ];
}

const sortTablesByName = (a: IPublishedConnectionTaskTableInfo, b: IPublishedConnectionTaskTableInfo) => (a.alias > b.alias) ? 1 : -1;

export type PublishedConnectionCreateOrEditTaskDialogContentProps = Readonly<{
  close: () => void;
  dismiss: () => void;
  publishedConnectionInfo: IPublishedConnectionDetailInfo;
  taskInfo?: IPublishedConnectionTaskInfo;
}>;

export function PublishedConnectionCreateOrEditTaskDialogContent(props: PublishedConnectionCreateOrEditTaskDialogContentProps): JSX.Element {
  const { close, dismiss, publishedConnectionInfo, taskInfo } = props;

  const [selectedSchedule, setSelectedSchedule] = React.useState<string | undefined>(taskInfo?.schedule.id);
  const [extractRefreshLevel, setExtractRefreshLevel] =
    React.useState<PublishedConnectionExtractRefreshLevel>(taskInfo?.extractRefreshLevel || PublishedConnectionExtractRefreshLevel.Database);
  const [editTablesDialogVisible, setEditTablesDialogVisibility] = React.useState(false);
  const [selectedTables, setSelectedTables] = React.useState<ReadonlyArray<IPublishedConnectionTaskTableInfo>>(taskInfo?.tables || []);
  const anchorRefForTableSelectionDialog = React.useRef(null);

  const availableTables = React.useMemo(
    () => publishedConnectionInfo.tables.map(table => ({
      alias: table.alias,
      luid: table.luid
    })).sort(sortTablesByName),
    [publishedConnectionInfo.tables]
  );

  const isGoButtonDisabled = React.useMemo(() => !selectedSchedule || selectedTables.length === 0, [selectedSchedule, selectedTables]);
  const handleSelectSchedule = React.useCallback((value) => setSelectedSchedule(value), [setSelectedSchedule]);
  const handleRadioSelect = React.useCallback((value) => setExtractRefreshLevel(value), [setExtractRefreshLevel]);

  const handleCreateTask = React.useCallback(
    () => {
      PublishedConnections.createExtractTask(
        publishedConnectionInfo.id,
        selectedSchedule,
        selectedTables.map(selectedTable => selectedTable.luid),
        ExtractTaskType.ExtractServiceFullRefresh,
        extractRefreshLevel
      ).then(result => {
        PublishedConnections.toast(result, Messages.createPublishedConnectionTaskSuccessful());
        if (!(result.errors && result.errors.length > 0)) {
          close();
        }
      });
    },
    [close, extractRefreshLevel, publishedConnectionInfo.id, selectedSchedule, selectedTables]
  );

  const handleEditTask = React.useCallback(
    () => {
      if (!taskInfo) {
        return;
      }
      PublishedConnections.updateExtractTask(
        taskInfo.id,
        selectedSchedule,
        selectedTables.map(selectedTable => selectedTable.luid),
        ExtractTaskType.ExtractServiceFullRefresh,
        extractRefreshLevel
      ).then(result => {
        PublishedConnections.toast(result, Messages.editPublishedConnectionTaskSuccessful());
        if (!(result.errors && result.errors.length > 0)) {
          close();
        }
      });
    },
    [close, extractRefreshLevel, selectedSchedule, selectedTables, taskInfo]
  );

  const handleSelectTables = React.useCallback(
    (tables: ReadonlyArray<IPublishedConnectionTaskTableInfo>) => {
      setSelectedTables([...tables].sort(sortTablesByName));
      setEditTablesDialogVisibility(false);
    },
    [setEditTablesDialogVisibility, setSelectedTables]
  );

  const cancelSelectTables = React.useCallback(
    () => setEditTablesDialogVisibility(false),
    [setEditTablesDialogVisibility]
  );

  const toggleEditTablesDialogVisibility = React.useCallback(
    () => setEditTablesDialogVisibility(!editTablesDialogVisible),
    [editTablesDialogVisible, setEditTablesDialogVisibility]
  );

  return (
    <div className={rootClass}>
      <div className={topPaneClass}>
        <div className={dialogMessageClass}>
          {Messages.schedulePublishedConnectionChooseScheduleMessage()}
        </div>

        <div className={paddingBottomClass}>
          <PublishedConnectionTaskSchedulePicker
            scheduleId={selectedSchedule}
            onSelect={handleSelectSchedule}
          />
        </div>

        <RadioButtonWithLabelGroupWidget
          ariaLabel={Messages.publishedConnectionRefreshLevelRadioGroupLabel()}
          buttonList={createRadioButtonOptions()}
          className={paddingBottomClass}
          checkedIndex={extractRefreshLevel === PublishedConnectionExtractRefreshLevel.Database ? 0 : 1}
          handleChange={handleRadioSelect}
        />

        <div className={classes(refreshTypeButtonClass, paddingBottomClass)}>
          <PublishedConnectionTaskRefreshTypeDropdown/>
        </div>

        <div className={editTableLinkClass}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a ref={anchorRefForTableSelectionDialog}
             onClick={toggleEditTablesDialogVisibility}
             href='javascript:void(0)'>
            {Messages.publishedConnectionScheduledTaskAddEditTables()}
          </a>
        </div>

        {editTablesDialogVisible && (
          <PublishedConnectionScheduledTaskTableSelectionDialog
            anchorElementRef={anchorRefForTableSelectionDialog.current}
            onCancel={cancelSelectTables}
            onSelect={handleSelectTables}
            availableTables={availableTables}
            selectedTables={selectedTables}
          />
        )}

      </div>
      <div className={bottomPaneClass}>
        <div className={tablesContainerClass}>
          <PublishedConnectionScheduledTaskTablesGrid
            emptyGridMessage={Messages.publishedConnectionScheduledTaskNoTablesMessage()}
            tables={selectedTables}
          />
        </div>

        <div className={footerClass}>
          <DialogFooterButtonsWidget
            buttons={[{
              handleClick: dismiss,
              buttonType: ButtonType.Outline,
              children: Messages.cancel(),
              testId: 'schedule-published-connection-task-cancel'
            }, {
              handleClick: taskInfo ? handleEditTask : handleCreateTask,
              buttonType: ButtonType.Primary,
              children: taskInfo ? Messages.editTask() : Messages.schedulePublishedConnectionTask(),
              testId: 'schedule-published-connection-task-go',
              disabled: isGoButtonDisabled
            }]}
            testId={'schedule-published-connection-task-footer'}
          />
        </div>
      </div>
    </div>
  );
}
